// Backup and Restore Service for SAM
import { UserKeyService } from './userKeyService';
import { progressService } from './progressService';
import { apiService } from './api';
import { InventoryService } from './inventoryService';
import { EncryptionService } from './encryptionService';
import { STEP1_FIRST_STEP_INVENTORY_TYPE, STEP1_FIRST_STEP_VERSION } from '../types/step1Writing';
import { STEP1_ARCHIVE_VERSION_PREFIX } from '../utils/step1WritingArchive';
import { isDevelopment } from '../config/constants';
import { 
  supportsFileSystemAPI, 
  supportsWebShareAPI, 
  getBestExportMethod,
  getBestImportMethod,
  isMobile
} from '../utils/mobileDetection';

export interface BackupData {
  metadata: {
    exportDate: string;
    programTrack: string;
    // ❌ REMOVED: version, description, fieldStructure, encryptionKey
  };
  encryptedData: string; // Encrypted JSON containing all the actual data
}

// Interface for the decrypted data structure
export interface DecryptedBackupData {
  fieldStructure?: {
    steps: {
      [stepNumber: string]: {
        fields: FieldDefinition[];
        actions: string[];
        questions?: string[];
      }
    };
    inventories: {
      [type: string]: {
        fields: FieldDefinition[];
        requiredFields: string[];
      }
    };
  };
  progress: Record<string, any>;
  inventories: {
    resentments: any[];
    fears: any[];
    harms: any[];
    step1FirstStep?: any[];
    step1Archives?: Record<string, any[]>;
  };
}

export interface FieldDefinition {
  name: string;
  dataType: 'string' | 'number' | 'boolean' | 'date' | 'array';
  fieldType: 'text' | 'textarea' | 'checkbox' | 'select' | 'date';
  description: string;
  isRequired: boolean;
  isSensitive: boolean;
  validation?: string;
}

export interface ImportResult {
  success: boolean;
  method: string;
  data?: DecryptedBackupData; // Changed to DecryptedBackupData
  error?: string;
  summary?: {
    programTrack: string;
    exportDate: string;
    stepsWithData: number;
    totalInventories: number;
    successful: number;
    warnings: number;
    skipped: number;
  };
}

export class BackupService {
  /**
   * Generate default filename for export
   */
  static generateDefaultFilename(program: string, track: string): string {
    const now = new Date();
    const date = now.toISOString().split('T')[0]; // YYYY-MM-DD format
    const time = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).replace(/:/g, '-').replace(/\s/g, '');
    
    return `${program}-${track}-backup-${date}-${time}.json`;
  }

  /**
   * Get field structure for SAA 12-steps program
   */
  private static getFieldStructure() {
    return {
      steps: {
        '1': {
          fields: [
            { name: 'completed', dataType: 'boolean' as const, fieldType: 'checkbox' as const, description: 'Step completion status', isRequired: false, isSensitive: false },
            { name: 'started_at', dataType: 'date' as const, fieldType: 'date' as const, description: 'When step was started', isRequired: false, isSensitive: false },
            { name: 'personalNotes', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Personal reflections and notes', isRequired: false, isSensitive: true }
          ],
          actions: ['read_green_book', 'read_guidance']
        },
        '2': {
          fields: [
            { name: 'completed', dataType: 'boolean' as const, fieldType: 'checkbox' as const, description: 'Step completion status', isRequired: false, isSensitive: false },
            { name: 'started_at', dataType: 'date' as const, fieldType: 'date' as const, description: 'When step was started', isRequired: false, isSensitive: false },
            { name: 'step2_questions.q1', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Powerlessness question', isRequired: true, isSensitive: true },
            { name: 'step2_questions.q2', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Unmanageability question', isRequired: true, isSensitive: true },
            { name: 'step2_questions.q3', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Higher power question', isRequired: true, isSensitive: true },
            { name: 'personalNotes', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Personal reflections and notes', isRequired: false, isSensitive: true }
          ],
          actions: ['read_green_book', 'read_guidance'],
          questions: ['q1', 'q2', 'q3']
        },
        '3': {
          fields: [
            { name: 'completed', dataType: 'boolean' as const, fieldType: 'checkbox' as const, description: 'Step completion status', isRequired: false, isSensitive: false },
            { name: 'started_at', dataType: 'date' as const, fieldType: 'date' as const, description: 'When step was started', isRequired: false, isSensitive: false },
            { name: 'step3_questions.q1', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Turned over question', isRequired: true, isSensitive: true },
            { name: 'step3_questions.q2', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'God as understood question', isRequired: true, isSensitive: true },
            { name: 'step3_questions.q3', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Decision question', isRequired: true, isSensitive: true },
            { name: 'personalNotes', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Personal reflections and notes', isRequired: false, isSensitive: true }
          ],
          actions: ['read_green_book', 'read_guidance'],
          questions: ['q1', 'q2', 'q3']
        },
        '4': {
          fields: [
            { name: 'completed', dataType: 'boolean' as const, fieldType: 'checkbox' as const, description: 'Step completion status', isRequired: false, isSensitive: false },
            { name: 'started_at', dataType: 'date' as const, fieldType: 'date' as const, description: 'When step was started', isRequired: false, isSensitive: false },
            { name: 'personalNotes', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Personal reflections and notes', isRequired: false, isSensitive: true },
            { name: 'action3_scheduled', dataType: 'boolean' as const, fieldType: 'checkbox' as const, description: 'Action 3 scheduled checkbox', isRequired: false, isSensitive: false },
            { name: 'action3_place', dataType: 'boolean' as const, fieldType: 'checkbox' as const, description: 'Action 3 place checkbox', isRequired: false, isSensitive: false }
          ],
          actions: ['read_green_book', 'read_guidance', 'complete_inventories', 'schedule_with_sponsor']
        },
        '5': {
          fields: [
            { name: 'completed', dataType: 'boolean' as const, fieldType: 'checkbox' as const, description: 'Step completion status', isRequired: false, isSensitive: false },
            { name: 'started_at', dataType: 'date' as const, fieldType: 'date' as const, description: 'When step was started', isRequired: false, isSensitive: false },
            { name: 'personalNotes', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Personal reflections and notes', isRequired: false, isSensitive: true }
          ],
          actions: ['read_green_book', 'read_guidance']
        },
        '6': {
          fields: [
            { name: 'completed', dataType: 'boolean' as const, fieldType: 'checkbox' as const, description: 'Step completion status', isRequired: false, isSensitive: false },
            { name: 'started_at', dataType: 'date' as const, fieldType: 'date' as const, description: 'When step was started', isRequired: false, isSensitive: false },
            { name: 'characterDefects', dataType: 'array' as const, fieldType: 'select' as const, description: 'Character defects identified', isRequired: true, isSensitive: true },
            { name: 'defectAnswers', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Answers about character defects', isRequired: false, isSensitive: true },
            { name: 'personalNotes', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Personal reflections and notes', isRequired: false, isSensitive: true }
          ],
          actions: ['read_green_book', 'read_guidance']
        },
        '7': {
          fields: [
            { name: 'completed', dataType: 'boolean' as const, fieldType: 'checkbox' as const, description: 'Step completion status', isRequired: false, isSensitive: false },
            { name: 'started_at', dataType: 'date' as const, fieldType: 'date' as const, description: 'When step was started', isRequired: false, isSensitive: false },
            { name: 'personalNotes', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Personal reflections and notes', isRequired: false, isSensitive: true }
          ],
          actions: ['read_green_book', 'read_guidance']
        },
        '8': {
          fields: [
            { name: 'completed', dataType: 'boolean' as const, fieldType: 'checkbox' as const, description: 'Step completion status', isRequired: false, isSensitive: false },
            { name: 'started_at', dataType: 'date' as const, fieldType: 'date' as const, description: 'When step was started', isRequired: false, isSensitive: false },
            { name: 'personalNotes', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Personal reflections and notes', isRequired: false, isSensitive: true }
          ],
          actions: ['read_green_book', 'read_guidance']
        },
        '9': {
          fields: [
            { name: 'completed', dataType: 'boolean' as const, fieldType: 'checkbox' as const, description: 'Step completion status', isRequired: false, isSensitive: false },
            { name: 'started_at', dataType: 'date' as const, fieldType: 'date' as const, description: 'When step was started', isRequired: false, isSensitive: false },
            { name: 'personalNotes', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Personal reflections and notes', isRequired: false, isSensitive: true }
          ],
          actions: ['read_green_book', 'read_guidance']
        },
        '10': {
          fields: [
            { name: 'completed', dataType: 'boolean' as const, fieldType: 'checkbox' as const, description: 'Step completion status', isRequired: false, isSensitive: false },
            { name: 'started_at', dataType: 'date' as const, fieldType: 'date' as const, description: 'When step was started', isRequired: false, isSensitive: false },
            { name: 'personalNotes', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Personal reflections and notes', isRequired: false, isSensitive: true }
          ],
          actions: ['read_green_book', 'read_guidance']
        },
        '11': {
          fields: [
            { name: 'completed', dataType: 'boolean' as const, fieldType: 'checkbox' as const, description: 'Step completion status', isRequired: false, isSensitive: false },
            { name: 'started_at', dataType: 'date' as const, fieldType: 'date' as const, description: 'When step was started', isRequired: false, isSensitive: false },
            { name: 'prayers', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Prayers and meditation', isRequired: false, isSensitive: true },
            { name: 'personalNotes', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Personal reflections and notes', isRequired: false, isSensitive: true }
          ],
          actions: ['read_green_book', 'read_guidance']
        },
        '12': {
          fields: [
            { name: 'completed', dataType: 'boolean' as const, fieldType: 'checkbox' as const, description: 'Step completion status', isRequired: false, isSensitive: false },
            { name: 'started_at', dataType: 'date' as const, fieldType: 'date' as const, description: 'When step was started', isRequired: false, isSensitive: false },
            { name: 'personalNotes', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Personal reflections and notes', isRequired: false, isSensitive: true }
          ],
          actions: ['read_green_book', 'read_guidance']
        }
      },
      inventories: {
        resentments: {
          fields: [
            { name: 'who', dataType: 'string' as const, fieldType: 'text' as const, description: 'Who do I resent?', isRequired: true, isSensitive: true },
            { name: 'why', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Why do I resent them?', isRequired: true, isSensitive: true },
            { name: 'feeling', dataType: 'string' as const, fieldType: 'text' as const, description: 'How did that make me feel?', isRequired: true, isSensitive: true },
            { name: 'impacts', dataType: 'array' as const, fieldType: 'select' as const, description: 'What was lost, taken, or threatened inside me?', isRequired: true, isSensitive: true },
            { name: 'myPart', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'What was my part in this resentment?', isRequired: true, isSensitive: true }
          ],
          requiredFields: ['who', 'why', 'feeling', 'impacts', 'myPart']
        },
        fears: {
          fields: [
            { name: 'fear', dataType: 'string' as const, fieldType: 'text' as const, description: 'What do I fear?', isRequired: true, isSensitive: true },
            { name: 'why', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'Why do I have this fear?', isRequired: true, isSensitive: true },
            { name: 'impacts', dataType: 'array' as const, fieldType: 'select' as const, description: 'How does this fear affect me?', isRequired: true, isSensitive: true }
          ],
          requiredFields: ['fear', 'why', 'impacts']
        },
        harms: {
          fields: [
            { name: 'whoHarmed', dataType: 'string' as const, fieldType: 'text' as const, description: 'Who did I harm?', isRequired: true, isSensitive: true },
            { name: 'how', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'How did I harm them?', isRequired: true, isSensitive: true },
            { name: 'defects', dataType: 'array' as const, fieldType: 'select' as const, description: 'What part of my character caused this harm?', isRequired: true, isSensitive: true },
            { name: 'alternative', dataType: 'string' as const, fieldType: 'textarea' as const, description: 'What could I have done differently?', isRequired: true, isSensitive: true }
          ],
          requiredFields: ['whoHarmed', 'how', 'defects', 'alternative']
        }
      }
    };
  }

  /**
   * Export current progress with mobile optimization
   */
  static async exportProgress(
    program: string = 'SAA', 
    track: string = '12-steps',
    customFilename?: string
  ): Promise<{ success: boolean; method: string; error?: string }> {
    try {
      const programTrack = `${program}-${track}`;
      
      // Get user's encryption key
      const encryptionKey = await UserKeyService.getKey();
      
      // Get the key string from localStorage or DynamoDB instead of exporting the CryptoKey
      let keyString: string;
      try {
        // Try to get the key string directly from UserKeyService
        const userId = await apiService.whoami().then(user => user.email);
        const userKey = await apiService.getUserKey(userId);
        keyString = userKey?.encryptedKey || localStorage.getItem('sam_encryption_key') || '';
        
        if (!keyString) {
          throw new Error('No encryption key found');
        }
      } catch (error) {
        console.error('Failed to get key string:', error);
        throw new Error('Failed to export encryption key');
      }

      // Get all step progress with individual error handling
      const stepProgress: Record<string, any> = {};
      let decryptionErrors = 0;
      
      for (let step = 1; step <= 12; step++) {
        try {
          const progress = await progressService.getStepProgress(program, track, step);
          if (progress && (progress.completed || Object.keys(progress).length > 0)) {
            stepProgress[`step${step}`] = progress;
          }
        } catch (error) {
          console.error(`Error loading step ${step} progress:`, error);
          decryptionErrors++;
          // Continue with other steps even if one fails
          // Don't add this step to the backup if it can't be decrypted
        }
      }

      // Get inventory data with error handling
      let filteredResentments: any[] = [];
      let filteredFears: any[] = [];
      let filteredHarms: any[] = [];
      let step1FirstStep: any[] = [];
      let step1Archives: Record<string, any[]> = {};
      
      try {
        const [resentments, fears, harms] = await Promise.all([
          InventoryService.getInventory('resentments', 'resentments-current').catch(() => []),
          InventoryService.getInventory('fears', 'fears-current').catch(() => []),
          InventoryService.getInventory('harms', 'harms-current').catch(() => [])
        ]);

        // Include completion status items in export
        filteredResentments = resentments;
        filteredFears = fears;
        filteredHarms = harms;
      } catch (error) {
        console.error('Error loading inventory data:', error);
        // Continue with empty inventories if they can't be loaded
      }

      try {
        step1FirstStep = await InventoryService.getInventory(
          STEP1_FIRST_STEP_INVENTORY_TYPE,
          STEP1_FIRST_STEP_VERSION
        );
        const step1Versions = await InventoryService.listInventoryVersions(
          STEP1_FIRST_STEP_INVENTORY_TYPE
        ).catch(() => []);
        for (const version of step1Versions.filter((v) => v.startsWith(STEP1_ARCHIVE_VERSION_PREFIX))) {
          try {
            step1Archives[version] = await InventoryService.getInventory(
              STEP1_FIRST_STEP_INVENTORY_TYPE,
              version
            );
          } catch {
            // Skip unreadable archive versions
          }
        }
      } catch (error) {
        console.error('Error loading Step 1 writing data:', error);
      }

              // Prepare data for encryption
        const dataToEncrypt = {
          fieldStructure: this.getFieldStructure(),
          progress: stepProgress,
          inventories: {
            resentments: filteredResentments,
            fears: filteredFears,
            harms: filteredHarms,
            step1FirstStep,
            step1Archives,
          }
        };

        // Convert to JSON and encrypt
        const dataJson = JSON.stringify(dataToEncrypt);
        const encryptedData = await EncryptionService.encrypt(dataJson);

        // Create backup data with minimal metadata
        const backupData: BackupData = {
          metadata: {
            exportDate: new Date().toISOString(),
            programTrack,
          },
          encryptedData: encryptedData
        };

      // Check if we have any data to export
      const hasProgressData = Object.keys(stepProgress).length > 0;
      const hasInventoryData =
        filteredResentments.length > 0 ||
        filteredFears.length > 0 ||
        filteredHarms.length > 0 ||
        step1FirstStep.length > 0 ||
        Object.keys(step1Archives).length > 0;
      
      if (!hasProgressData && !hasInventoryData && isDevelopment()) {
        console.log('No data found to export - creating minimal backup with metadata only');
        // Still create the backup file but with empty data
      }

      const jsonData = JSON.stringify(backupData, null, 2);
      const filename = customFilename || this.generateDefaultFilename(program, track);
      const blob = new Blob([jsonData], { type: 'application/json' });
      const file = new File([blob], filename, { type: 'application/json' });

      // Try different export methods based on device capabilities
      const exportMethod = getBestExportMethod();
      if (isDevelopment()) {
        console.log('🔧 Export method selected:', exportMethod);
        console.log('🔧 File System API supported:', supportsFileSystemAPI());
        console.log('🔧 Web Share API supported:', supportsWebShareAPI());
        console.log('🔧 Is mobile:', isMobile());
      }

      if (exportMethod === 'native-share' && supportsWebShareAPI()) {
        try {
          if (isDevelopment()) console.log('🔧 Trying native share...');
          await navigator.share({
            files: [file],
            title: 'SAM Progress Backup',
            text: 'My SAM recovery progress backup'
          });
          return { success: true, method: 'native-share' };
        } catch (error) {
          if (isDevelopment()) console.log('🔧 Native share failed, falling back to file system:', error);
        }
      }

      if (exportMethod === 'file-system' && supportsFileSystemAPI()) {
        try {
          if (isDevelopment()) console.log('🔧 Trying File System API...');
          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'SAM Backup File',
              accept: { 'application/json': ['.json'] }
            }]
          });
          const writable = await handle.createWritable();
          await writable.write(jsonData);
          await writable.close();
          if (isDevelopment()) console.log('🔧 File System API succeeded');
          return { success: true, method: 'file-system' };
        } catch (error) {
          if (isDevelopment()) console.log('🔧 File System API failed, falling back to download:', error);
        }
      }

      // Fallback to traditional download
      if (isDevelopment()) console.log('🔧 Using fallback download method');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      return { success: true, method: 'download' };

    } catch (error) {
      console.error('Export failed:', error);
      
      // Check if this is a decryption error and provide a helpful message
      if (error instanceof Error && error.message.includes('decrypt')) {
        return { 
          success: false, 
          method: 'failed', 
          error: 'Some of your data could not be decrypted. This might be due to a key change. You can still export what data is available, or try clearing and starting fresh.' 
        };
      }
      
      return { 
        success: false, 
        method: 'failed', 
        error: error instanceof Error ? error.message : 'Export failed' 
      };
    }
  }

  /**
   * Import progress with validation and mobile optimization
   */
  static async importProgress(): Promise<ImportResult> {
    try {
      const importMethod = getBestImportMethod();
      let file: File;

      if (importMethod === 'file-system' && supportsFileSystemAPI()) {
        try {
          const [fileHandle] = await (window as any).showOpenFilePicker({
            types: [{
              description: 'SAM Backup Files',
              accept: { 'application/json': ['.json'] }
            }],
            multiple: false
          });
          file = await fileHandle.getFile();
        } catch (error) {
          throw new Error('File selection cancelled or failed');
        }
      } else {
        // Traditional file input
        file = await this.selectFileWithInput();
      }

      if (!file) {
        throw new Error('No file selected');
      }

      // Read and validate file
      const content = await file.text();
      const data: BackupData = JSON.parse(content);

      // Validate backup data with gracious handling
      if (!data.metadata) {
        throw new Error('Invalid backup file: Missing metadata');
      }

      // Check if this is the new encrypted format or old format
      let decryptedData: DecryptedBackupData;
      
      if (data.encryptedData) {
        // New format - decrypt the data
        try {
          const decryptedJson = await EncryptionService.decrypt(data.encryptedData);
          decryptedData = JSON.parse(decryptedJson);
        } catch (error) {
          throw new Error('Failed to decrypt backup data. This file may be corrupted or from a different user.');
        }
      } else if ((data as any).progress && (data as any).inventories) {
        // Old format - convert to new structure
        decryptedData = {
          fieldStructure: (data.metadata as any).fieldStructure,
          progress: (data as any).progress,
          inventories: (data as any).inventories
        };
      } else {
        throw new Error('Invalid backup file format');
      }

      // Create summary
      const stepsWithData = Object.keys(decryptedData.progress || {}).length;
      const totalInventories = (
        (decryptedData.inventories?.resentments?.length || 0) +
        (decryptedData.inventories?.fears?.length || 0) +
        (decryptedData.inventories?.harms?.length || 0)
      );

      const summary = {
        programTrack: data.metadata.programTrack || 'unknown',
        exportDate: data.metadata.exportDate || 'unknown',
        stepsWithData,
        totalInventories,
        successful: 0,
        warnings: 0,
        skipped: 0
      };

      return {
        success: true,
        method: importMethod,
        data: decryptedData, // Return the decrypted data instead of the original
        summary
      };

    } catch (error) {
      console.error('Import failed:', error);
      return {
        success: false,
        method: 'failed',
        error: error instanceof Error ? error.message : 'Import failed'
      };
    }
  }

  /**
   * Select file using traditional input (fallback)
   */
  private static selectFileWithInput(): Promise<File> {
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json,application/json';
      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          resolve(file);
        } else {
          reject(new Error('No file selected'));
        }
      };
      input.oncancel = () => reject(new Error('File selection cancelled'));
      input.click();
    });
  }

  /**
   * Clear current progress with confirmation
   */
  static async clearProgress(program: string = 'SAA', track: string = '12-steps'): Promise<{ success: boolean; error?: string }> {
    try {
      // Clear all step progress by setting them to null/undefined to indicate "not started"
      for (let step = 1; step <= 12; step++) {
        try {
          await progressService.updateStepProgress({
            program,
            track,
            step_number: step,
            step_title: `Step ${step}`,
            completed: false,
            data: { actions: [] } // Set empty actions array to ensure "not started" status
          });
        } catch (error) {
          console.error(`Error clearing step ${step}:`, error);
        }
      }

      // Clear inventories by saving empty arrays
      await Promise.all([
        InventoryService.saveInventory('resentments', 'resentments-current', []),
        InventoryService.saveInventory('fears', 'fears-current', []),
        InventoryService.saveInventory('harms', 'harms-current', [])
      ].map(promise => promise.catch(error => console.error('Error clearing inventory:', error))));

      return { success: true };
    } catch (error) {
      console.error('Clear progress failed:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Clear failed' 
      };
    }
  }

  /**
   * Apply imported data to the system with best-effort matching
   */
  static async applyImportedData(data: BackupData | DecryptedBackupData, selectedProgramTrack?: any): Promise<{ success: boolean; error?: string; results?: { successful: number; warnings: number; skipped: number } }> {
    if (isDevelopment()) console.log('🔍 applyImportedData called with:', { data, selectedProgramTrack });
    
    try {
      // Decrypt data if it's in the new format
      let decryptedData: DecryptedBackupData;
      
      if ('encryptedData' in data && data.encryptedData) {
        // New format - decrypt the data
        try {
          const decryptedJson = await EncryptionService.decrypt(data.encryptedData);
          decryptedData = JSON.parse(decryptedJson);
        } catch (error) {
          throw new Error('Failed to decrypt backup data. This file may be corrupted or from a different user.');
        }
      } else if ('progress' in data && 'inventories' in data) {
        // Already decrypted format
        decryptedData = data as DecryptedBackupData;
      } else {
        throw new Error('Invalid backup file format');
      }

      // Always use best-effort import when selectedProgramTrack is provided
      if (selectedProgramTrack) {
        if (isDevelopment()) console.log('🔍 Using applyBestEffortImport with selectedProgramTrack:', selectedProgramTrack);
        const results = await this.applyBestEffortImport(decryptedData, selectedProgramTrack);
        if (isDevelopment()) console.log('🔍 applyBestEffortImport completed with results:', results);
        return { 
          success: true, 
          results 
        };
      }
      
      // Fallback to original logic for backward compatibility (when no selectedProgramTrack)
      const [program, track] = ('metadata' in data) ? 
        data.metadata.programTrack.split('-') : 
        ['SAA', '12-steps'];
      
      // Apply step progress
      for (const [stepKey, stepData] of Object.entries(decryptedData.progress)) {
        const stepNumber = parseInt(stepKey.replace('step', ''));
        if (!isNaN(stepNumber)) {
          await progressService.updateStepProgress({
            program,
            track,
            step_number: stepNumber,
            step_title: `Step ${stepNumber}`,
            completed: (stepData as any).completed || false,
            data: stepData
          });
        }
      }

      // Apply inventories
      if (decryptedData.inventories) {
        if (decryptedData.inventories.resentments?.length > 0) {
          await InventoryService.saveInventory('resentments', 'resentments-current', decryptedData.inventories.resentments);
        }

        if (decryptedData.inventories.fears?.length > 0) {
          await InventoryService.saveInventory('fears', 'fears-current', decryptedData.inventories.fears);
        }

        if (decryptedData.inventories.harms?.length > 0) {
          await InventoryService.saveInventory('harms', 'harms-current', decryptedData.inventories.harms);
        }

        if ((decryptedData.inventories.step1FirstStep?.length ?? 0) > 0) {
          await InventoryService.saveInventory(
            STEP1_FIRST_STEP_INVENTORY_TYPE,
            STEP1_FIRST_STEP_VERSION,
            decryptedData.inventories.step1FirstStep!
          );
        }
        if (decryptedData.inventories.step1Archives) {
          for (const [version, items] of Object.entries(decryptedData.inventories.step1Archives)) {
            if (items?.length > 0) {
              await InventoryService.saveInventory(
                STEP1_FIRST_STEP_INVENTORY_TYPE,
                version,
                items
              );
            }
          }
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Apply imported data failed:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Apply failed' 
      };
    }
  }

  /**
   * Check if current program/track has data
   */
  static async hasCurrentData(program: string = 'SAA', track: string = '12-steps'): Promise<boolean> {
    try {
      const earliest = await this.getEarliestMeaningfulStepWorkDate(program, track);
      if (earliest) return true;

      const [resentments, fears, harms, step1] = await Promise.all([
        InventoryService.getInventory('resentments', 'resentments-current').catch(() => []),
        InventoryService.getInventory('fears', 'fears-current').catch(() => []),
        InventoryService.getInventory('harms', 'harms-current').catch(() => []),
        InventoryService.getInventory(STEP1_FIRST_STEP_INVENTORY_TYPE, STEP1_FIRST_STEP_VERSION).catch(
          () => []
        ),
      ]);

      return resentments.length > 0 || fears.length > 0 || harms.length > 0 || step1.length > 0;
    } catch (error) {
      console.error('Error checking current data:', error);
      return false;
    }
  }

  /**
   * Earliest timestamp for meaningful step work on this program/track (progress only).
   */
  static async getEarliestMeaningfulStepWorkDate(
    program: string = 'SAA',
    track: string = '12-steps'
  ): Promise<string | null> {
    try {
      let earliestMs: number | null = null;

      const consider = (progress: { updated_at?: string; started_at?: string; created_at?: string } | null) => {
        if (!progress) return;
        for (const iso of [progress.updated_at, progress.started_at, progress.created_at]) {
          if (!iso) continue;
          const ms = Date.parse(iso);
          if (!Number.isNaN(ms)) {
            earliestMs = earliestMs === null ? ms : Math.min(earliestMs, ms);
          }
        }
      };

      const gettingStarted = await progressService.getStepProgress(program, track, 0).catch(() => null);
      if (gettingStarted) {
        const gsActions = gettingStarted.data?.actions;
        const hasGsWork =
          gettingStarted.completed ||
          (Array.isArray(gsActions) && gsActions.some((action: { completed?: boolean }) => action.completed)) ||
          Boolean(gettingStarted.data?.three_circles);
        if (hasGsWork) consider(gettingStarted);
      }

      for (let step = 1; step <= 12; step++) {
        const progress = await progressService.getStepProgress(program, track, step).catch(() => null);
        if (!progress) continue;
        const hasStepWork =
          progress.completed ||
          (Array.isArray(progress.data?.actions) &&
            progress.data.actions.some((action: { completed?: boolean }) => action.completed));
        if (hasStepWork) consider(progress);
      }

      return earliestMs !== null ? new Date(earliestMs).toISOString() : null;
    } catch (error) {
      console.error('Error getting earliest step work date:', error);
      return null;
    }
  }

  /** Workshop banner: prior step work started well before workshop interest/confirm. */
  static async hasPreWorkshopStepWork(
    workshopEngagedAt: string | null | undefined,
    graceDays = 7
  ): Promise<boolean> {
    if (!workshopEngagedAt) return false;
    const engagedMs = Date.parse(workshopEngagedAt);
    if (Number.isNaN(engagedMs)) return false;

    const hasData = await this.hasCurrentData('SAA', '12-steps');
    if (!hasData) return false;

    const earliest = await this.getEarliestMeaningfulStepWorkDate('SAA', '12-steps');
    if (!earliest) return false;

    const earliestMs = Date.parse(earliest);
    if (Number.isNaN(earliestMs)) return false;

    const graceMs = graceDays * 24 * 60 * 60 * 1000;
    return earliestMs < engagedMs - graceMs;
  }

  /**
   * Apply best-effort import with simplified data mapping
   */
  private static async applyBestEffortImport(data: DecryptedBackupData, selectedProgramTrack: any): Promise<{ successful: number; warnings: number; skipped: number }> {
    const results = { successful: 0, warnings: 0, skipped: 0 };
    
    try {
      // Extract program and track from the selected program track
      const program = selectedProgramTrack.program;
      const track = selectedProgramTrack.track;
      
      if (isDevelopment()) {
        console.log('🔍 Import Debug - Data structure:', data);
        console.log('🔍 Import Debug - Progress keys:', Object.keys(data.progress));
      }
      
      // Apply step progress with simplified data mapping
      for (const [stepKey, stepData] of Object.entries(data.progress)) {
        const stepNumber = parseInt(stepKey.replace('step', ''));
        if (isDevelopment()) console.log(`🔍 Import Debug - Processing step ${stepNumber}:`, stepKey, stepData);
        
        if (!isNaN(stepNumber)) {
          try {
            // Simplified data extraction - handle all possible nested structures
            let stepDataContent = stepData;
            
            // If stepData has a 'data' property, use that as the main content
            if (stepData.data) {
              stepDataContent = stepData.data;
            }
            
            // If stepDataContent has another nested 'data' property, use that
            if (stepDataContent.data) {
              stepDataContent = stepDataContent.data;
            }
            
            if (isDevelopment()) console.log(`🔍 Import Debug - Step ${stepNumber} extracted content:`, stepDataContent);
            
            // Extract all possible data fields with fallbacks
            const actions = stepDataContent.actions || [];
            const answers = stepDataContent.answers || stepDataContent.step2_questions || stepDataContent.step3_questions || {};
            const characterDefects = stepDataContent.characterDefects || [];
            const defectAnswers = stepDataContent.defectAnswers || {};
            const prayers = stepDataContent.prayers || [];
            const reflections = stepDataContent.reflections || [];
            const personalNotes = stepDataContent.personalNotes || {};
            const sponsorNotes = stepDataContent.sponsorNotes || {};
            
            // Extract Step 4 specific checkbox data
            const action3_scheduled = stepDataContent.action3_scheduled;
            const action3_place = stepDataContent.action3_place;
            
            // Create clean data structure that matches what the progress service expects
            const cleanData = {
              actions,
              answers,
              characterDefects,
              defectAnswers,
              prayers,
              reflections,
              personalNotes,
              sponsorNotes,
              // Include Step 4 specific data if it exists
              ...(action3_scheduled !== undefined && { action3_scheduled }),
              ...(action3_place !== undefined && { action3_place })
            };
            
            const updateData = {
              program,
              track,
              step_number: stepNumber,
              step_title: `Step ${stepNumber}`,
              completed: stepData.completed || false,
              data: cleanData
            };
            
            if (isDevelopment()) console.log(`🔍 Import Debug - Updating step ${stepNumber} with:`, updateData);
            
            // Use the standard saving mechanism which handles encryption properly
            const result = await progressService.updateStepProgress(updateData);
            if (isDevelopment()) {
              console.log(`🔍 Import Debug - Step ${stepNumber} save result:`, result);
              console.log(`✅ Import Debug - Successfully imported step ${stepNumber}`);
            }
            results.successful++;
          } catch (error) {
            console.error(`❌ Import Debug - Error importing step ${stepNumber}:`, error);
            results.skipped++;
          }
        }
      }
      
      // Apply inventories
      if (data.inventories) {
        if (data.inventories.resentments?.length > 0) {
          try {
            await InventoryService.saveInventory('resentments', 'resentments-current', data.inventories.resentments);
            results.successful++;
          } catch (error) {
            console.error('Error importing resentments:', error);
            results.skipped++;
          }
        }
        
        if (data.inventories.fears?.length > 0) {
          try {
            await InventoryService.saveInventory('fears', 'fears-current', data.inventories.fears);
            results.successful++;
          } catch (error) {
            console.error('Error importing fears:', error);
            results.skipped++;
          }
        }
        
        if (data.inventories.harms?.length > 0) {
          try {
            await InventoryService.saveInventory('harms', 'harms-current', data.inventories.harms);
            results.successful++;
          } catch (error) {
            console.error('Error importing harms:', error);
            results.skipped++;
          }
        }

        if ((data.inventories.step1FirstStep?.length ?? 0) > 0) {
          try {
            await InventoryService.saveInventory(
              STEP1_FIRST_STEP_INVENTORY_TYPE,
              STEP1_FIRST_STEP_VERSION,
              data.inventories.step1FirstStep!
            );
            results.successful++;
          } catch (error) {
            console.error('Error importing Step 1 writing:', error);
            results.skipped++;
          }
        }
        if (data.inventories.step1Archives) {
          for (const [version, items] of Object.entries(data.inventories.step1Archives)) {
            if (items?.length > 0) {
              try {
                await InventoryService.saveInventory(
                  STEP1_FIRST_STEP_INVENTORY_TYPE,
                  version,
                  items
                );
                results.successful++;
              } catch (error) {
                console.error(`Error importing Step 1 archive ${version}:`, error);
                results.skipped++;
              }
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Apply imported data failed:', error);
      throw error;
    }
    
    return results;
  }
} 